import React from 'react';
import { Box, Button, Chip, Stack, Tooltip, Typography, alpha, keyframes } from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { useNavigate } from 'react-router-dom';
import { useBoardId } from '../../ui/BootstrapProvider';
import { useRefreshController } from './RefreshController';
import { formatRelativeTime } from '../../utils/date';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

interface ScreenDef {
  key: string;
  label: string;
  path: string;
  locked?: boolean;
  lockedNote?: string;
}

const SCREENS: ScreenDef[] = [
  { key: 'pulse', label: 'Pulse', path: '/monitoring/contact-optimization' },
  { key: 'timing', label: 'Timing', path: '/monitoring/contact-optimization/timing' },
  { key: 'plan', label: 'Plan', path: '/monitoring/contact-optimization/plan' },
  { key: 'queue', label: 'Queue', path: '/monitoring/contact-optimization/queue' },
  { key: 'scorecard', label: 'Scorecard', path: '/monitoring/contact-optimization/scorecard' },
];

// The prediction model behind these tabs now lives under Predictive
// Models (Intelligence > Predictive Models > Contact Optimization
// Scoring), not as a tab here -- it's a model artifact, not a working
// view of "today's contact rate", so it fits that section's job better.
// See ModelPage.tsx, now routed at /predictive-models/contact-optimization.
export default function ContactOptimizationNav({ active }: { active: 'pulse' | 'timing' | 'plan' | 'queue' | 'scorecard' }) {
  const navigate = useNavigate();
  // Where the workspace has the Scorecard board, the Scorecard lives there and
  // this bar stops offering it; the old address still opens, onto a notice
  // pointing at the board. Every other tab stays, whatever the sidebar shows:
  // a sidebar may list only the main pages (the customer's Sales: Pulse, Plan) and
  // leave Timing and Queue one tab away for whoever wants them.
  const scorecardMoved = useBoardId('sales_scorecard') !== null;
  const screens = React.useMemo(
    () => (scorecardMoved ? SCREENS.filter((screen) => screen.key !== 'scorecard') : SCREENS),
    [scorecardMoved],
  );
  const refreshCtx = useRefreshController();
  const refreshing = refreshCtx?.refreshing ?? false;
  const lastUpdated = refreshCtx?.lastUpdated ?? null;

  // Re-renders every 30s purely so "Updated 4 minutes ago" keeps counting
  // up on its own, without needing a refresh or any other state change to
  // trigger it.
  const [, forceTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const updatedLabel = lastUpdated ? formatRelativeTime(lastUpdated) : null;
  const updatedExact = lastUpdated
    ? new Date(lastUpdated).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    // Sticky, not fixed -- docks to the top of this page's own scroll
    // area (DashboardLayout's content pane) the moment it would
    // otherwise scroll out of view, but still lays out in-flow above the
    // fold, so nothing needs a manual top-offset. `zIndex` + an opaque
    // background (matching the page canvas, not transparent) so chart/
    // table content scrolling underneath never shows through the tabs.
    <Box
      sx={(t) => ({
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: t.tokens.surface.canvas,
        mb: 2,
      })}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={(t) => ({ borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {screens.map((screen) => {
            const isActive = screen.key === active;
            if (screen.locked) {
              return (
                <Tooltip key={screen.key} title={screen.lockedNote ?? 'Not built yet'} arrow>
                  <Chip
                    label={screen.label}
                    size="small"
                    variant="outlined"
                    sx={{ opacity: 0.45, mb: 1, cursor: 'default' }}
                  />
                </Tooltip>
              );
            }
            return (
              <Box
                key={screen.key}
                component="button"
                onClick={() => navigate(screen.path)}
                sx={(t) => ({
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  px: 1.5,
                  py: 1,
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? t.palette.text.primary : t.palette.text.secondary,
                  borderBottom: isActive ? `2px solid ${t.palette.primary.main}` : '2px solid transparent',
                  transition: 'color 0.15s ease',
                  '&:hover': { color: t.palette.text.primary, bgcolor: alpha(t.palette.text.primary, 0.03) },
                })}
              >
                {screen.label}
              </Box>
            );
          })}
        </Stack>
        {refreshCtx && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 0.5 }}>
            {updatedLabel && (
              <Tooltip title={`Data last computed ${updatedExact}`} arrow>
                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', whiteSpace: 'nowrap' }}>
                  Data as of {updatedLabel}
                </Typography>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => refreshCtx.triggerRefresh()}
              disabled={refreshing}
              startIcon={
                <RefreshRounded sx={{ fontSize: 16, animation: refreshing ? `${spin} 0.9s linear infinite` : 'none' }} />
              }
              sx={{ fontSize: 12, py: 0.375, px: 1.25, lineHeight: 1.4 }}
            >
              {refreshing ? 'Refreshing\u2026' : 'Refresh'}
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
